"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Icon } from "../icon";
import { RequiredMark } from "../required-mark";
import { cn } from "../utils";
import type { TextareaProps } from "./textarea.types";

/**
 * Textarea Component
 *
 * V&A Waterfront Design System - Textarea Component
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4?node-id=10742-20452
 *
 * Multi-line text entry with auto-expand, character count, and error states.
 *
 * @example
 * // Basic usage
 * <Textarea label="Message" placeholder="Type your message..." />
 *
 * // With character count
 * <Textarea label="Bio" maxLength={200} showCount />
 *
 * // Auto-expanding
 * <Textarea label="Description" autoExpand />
 *
 * // With error
 * <Textarea label="Comment" errorText="This field is required" />
 *
 * // On dark background
 * <Textarea label="Feedback" onDark />
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((t0, ref) => {
  const $ = _c(80);
  let className;
  let defaultValue;
  let disabled;
  let errorText;
  let helperText;
  let id;
  let label;
  let maxLength;
  let onChange;
  let props;
  let t1;
  let t2;
  let t3;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      label,
      helperText,
      errorText,
      maxLength,
      showCount: t1,
      autoExpand: t2,
      onDark: t3,
      disabled,
      id,
      onChange,
      value,
      defaultValue,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = defaultValue;
    $[3] = disabled;
    $[4] = errorText;
    $[5] = helperText;
    $[6] = id;
    $[7] = label;
    $[8] = maxLength;
    $[9] = onChange;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = value;
  } else {
    className = $[1];
    defaultValue = $[2];
    disabled = $[3];
    errorText = $[4];
    helperText = $[5];
    id = $[6];
    label = $[7];
    maxLength = $[8];
    onChange = $[9];
    props = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    value = $[14];
  }
  const showCount = t1 === undefined ? false : t1;
  const autoExpand = t2 === undefined ? true : t2;
  const onDark = t3 === undefined ? false : t3;
  const internalRef = React.useRef(null);
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const descriptionId = `${inputId}-description`;
  const hasError = Boolean(errorText);
  let t4;
  if ($[15] !== defaultValue || $[16] !== value) {
    t4 = () => {
      const initial = (value ?? defaultValue ?? "") as string;
      return initial.length;
    };
    $[15] = defaultValue;
    $[16] = value;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const [charCount, setCharCount] = React.useState(t4);
  let t5;
  let t6;
  if ($[18] !== value) {
    t5 = () => {
      if (value !== undefined) {
        setCharCount(String(value).length);
      }
    };
    t6 = [value];
    $[18] = value;
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[21] !== ref) {
    t7 = node => {
      internalRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      }
    };
    $[21] = ref;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const setRefs = t7;
  let t8;
  if ($[23] !== autoExpand) {
    t8 = () => {
      const el = internalRef.current;
      if (autoExpand && el) {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      }
    };
    $[23] = autoExpand;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const adjustHeight = t8;
  let t9;
  if ($[25] !== adjustHeight) {
    t9 = () => {
      adjustHeight();
    };
    $[25] = adjustHeight;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== adjustHeight || $[28] !== value) {
    t10 = [adjustHeight, value];
    $[27] = adjustHeight;
    $[28] = value;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[30] !== adjustHeight || $[31] !== onChange) {
    t11 = e => {
      setCharCount(e.target.value.length);
      adjustHeight();
      onChange?.(e);
    };
    $[30] = adjustHeight;
    $[31] = onChange;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const handleChange = t11;
  const showCharacterCount = showCount && maxLength !== undefined;
  const isOverLimit = maxLength !== undefined && charCount > maxLength;
  const t12 = onDark && "dark";
  let t13;
  if ($[33] !== className || $[34] !== t12) {
    t13 = cn("group/textarea flex w-full flex-col gap-2", t12, className);
    $[33] = className;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = disabled ? "text-input-text-disabled" : "text-input-text-label";
  let t15;
  if ($[36] !== t14) {
    t15 = cn("typography-component-sm-medium desktop:typography-component-md-medium", t14);
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== props.required) {
    t16 = <RequiredMark required={props.required} />;
    $[38] = props.required;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== inputId || $[41] !== label || $[42] !== t15 || $[43] !== t16) {
    t17 = <label htmlFor={inputId} className={t15}>{label}{t16}</label>;
    $[40] = inputId;
    $[41] = label;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const t18 = helperText || errorText ? descriptionId : undefined;
  const t19 = hasError || undefined;
  const t20 = value === undefined ? defaultValue : undefined;
  const t21 = (hasError || isOverLimit) && "bg-input-surface-error text-input-text-error placeholder:text-input-text-error";
  const t22 = hasError && "pr-12";
  const t23 = autoExpand ? "resize-none overflow-hidden" : "resize-y";
  let t24;
  if ($[45] !== t21 || $[46] !== t22 || $[47] !== t23) {
    t24 = cn("block w-full rounded-sm border border-transparent px-4 pt-3 pb-6", "typography-body-sm-regular text-input-text-rest placeholder:text-input-text-disabled", "bg-input-surface-rest", "outline-none", "hover:bg-input-surface-hover", "focus:border-border-subtle focus:bg-input-surface-hover [.dark_&]:focus:border-transparent", "disabled:pointer-events-none disabled:bg-input-surface-disabled disabled:text-input-text-disabled", t21, t22, t23, "min-h-[108px]", "whitespace-pre-wrap");
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== disabled || $[50] !== handleChange || $[51] !== inputId || $[52] !== maxLength || $[53] !== props || $[54] !== setRefs || $[55] !== t18 || $[56] !== t19 || $[57] !== t20 || $[58] !== t24 || $[59] !== value) {
    t25 = <textarea ref={setRefs} id={inputId} disabled={disabled} aria-describedby={t18} aria-invalid={t19} maxLength={maxLength} value={value} defaultValue={t20} onChange={handleChange} className={t24} {...props} />;
    $[49] = disabled;
    $[50] = handleChange;
    $[51] = inputId;
    $[52] = maxLength;
    $[53] = props;
    $[54] = setRefs;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t24;
    $[59] = value;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== hasError) {
    t26 = hasError && <Icon name="error" filled={true} size="lg" className="text-input-text-error absolute right-4 top-3 pointer-events-none" decorative={true} />;
    $[61] = hasError;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t25 || $[64] !== t26) {
    t27 = <div className="relative">{t25}{t26}</div>;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== charCount || $[67] !== descriptionId || $[68] !== errorText || $[69] !== hasError || $[70] !== helperText || $[71] !== isOverLimit || $[72] !== maxLength || $[73] !== showCharacterCount) {
    t28 = (hasError || helperText || showCharacterCount) && <div className="flex items-start justify-between gap-2"><div className="flex-1">{hasError ? <p id={descriptionId} role="alert" className="typography-component-sm-regular desktop:typography-component-md-regular text-input-text-error">{errorText}</p> : helperText ? <p id={descriptionId} className="typography-component-sm-regular desktop:typography-component-md-regular text-input-text-label">{helperText}</p> : null}</div>{showCharacterCount && <span className={cn("typography-component-sm-regular desktop:typography-component-md-regular shrink-0", isOverLimit ? "text-input-text-error" : "text-input-text-label")} role="status" aria-live="polite" aria-atomic="true">{charCount}/{maxLength}</span>}</div>;
    $[66] = charCount;
    $[67] = descriptionId;
    $[68] = errorText;
    $[69] = hasError;
    $[70] = helperText;
    $[71] = isOverLimit;
    $[72] = maxLength;
    $[73] = showCharacterCount;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t13 || $[76] !== t17 || $[77] !== t27 || $[78] !== t28) {
    t29 = <div data-testid="textarea" className={t13}>{t17}{t27}{t28}</div>;
    $[75] = t13;
    $[76] = t17;
    $[77] = t27;
    $[78] = t28;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  return t29;
});
Textarea.displayName = "Textarea";
export { Textarea };